import { useTranslation } from 'react-i18next';
import { IColumnType, Table } from '../../../../../components/Table/Table';
import { IInitialState } from '../../../../../reduxService';
import { useAppSelector } from '../../../../../reduxService/hooks';
import { roundToTwo } from '../../../../MACRA_1/components/InputForm/Geometry/utils';
import { getActionByProjectType } from '../../../../actionMap';
import { conversionLabel } from '../../../../conversion';
import { selectProjectInfo } from '../../../../store/projectInfo/projectInfoSlice';
import { ISteppedGabionResults } from '../../../api/IApiResponse';
import { IProjectMacra2 } from '../../../store/project/projectSlice';
import { IHydraulicDataSteppedWeirForm } from '../../InputForm/HydraulicData/IHydraulicData';
import { IWeirGeometrySteppedWeirForm } from '../../InputForm/WeirGeometry/IWeirGeometry';
import { IWingsGeometrySteppedWeirForm } from '../../InputForm/WingsGeometry/IWingsGeometry';
import { IData } from './HydraulicVertical';
import SmallLetter from '../../../../../components/SmallLetter/SmallLetter';
import { IStillingBasinSteppedWeirSlopedWeirForm } from '../../InputForm/StillingBasin/IStillingBasin';

interface IHydraulicSteppedProps {}

const HydraulicStepped = (props: IHydraulicSteppedProps) => {
    const { t } = useTranslation();
    const { selectResults, selectProject } = getActionByProjectType();
    const results: IInitialState<ISteppedGabionResults> = useAppSelector(selectResults as any);
    const project: IInitialState<IProjectMacra2> = useAppSelector(selectProject as any);
    const projectInfo = useAppSelector(selectProjectInfo);

    const symbolValueUnitColumns: IColumnType<IData>[] = [
        {
            key: 'symbol',
            title: t('output_symbol'),
            width: '15%',
        },
        {
            key: 'value',
            title: t('output_value'),
            width: '20%',
            render: (_, row) => {
                if (typeof row.value === 'boolean') {
                    if (row.value) return <i className='fa-solid fa-check d-flex justify-content-center align-items-center' style={{ color: '#004652' }} />;
                    else return <i className='fa-solid fa-x d-flex justify-content-center align-items-center' style={{ color: '#E31937' }} />;
                } else {
                    return row.value;
                }
            },
        },
        {
            key: 'unit',
            title: t('output_unit'),
            width: '15%',
        },
    ];
    const columnsInput: IColumnType<IData>[] = [
        {
            key: 'label',
            title: t('output_table_title_input'),
        },
        ...symbolValueUnitColumns,
    ];
    const columnsUpstream: IColumnType<IData>[] = [
        {
            key: 'label',
            title: t('output_hydraulic_sloped_upstream_parameters'),
        },
        ...symbolValueUnitColumns,
    ];
    const columnsDownstream: IColumnType<IData>[] = [
        {
            key: 'label',
            title: t('output_hydraulic_sloped_downstream_parameters'),
        },
        ...symbolValueUnitColumns,
    ];

    const input: IData[] = [
        {
            label: t('output_hydraulic_stepped_crest_width'),
            symbol: <SmallLetter normal='L' small='g' />,
            value: roundToTwo((project.data?.wingsGeometry as IWingsGeometrySteppedWeirForm).width),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_stepped_toe_width'),
            symbol: <SmallLetter normal='L' small='t' />,
            value: roundToTwo((project.data?.stillingBasin as IStillingBasinSteppedWeirSlopedWeirForm).width),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_stepped_step_height'),
            symbol: 'h',
            value: roundToTwo((project.data?.weirGeometry as IWeirGeometrySteppedWeirForm).stepHeight),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_stepped_total_height'),
            symbol: <SmallLetter normal='f' small='g' />,
            value: roundToTwo(
                ((project.data?.weirGeometry as IWeirGeometrySteppedWeirForm).stepNumber! - (project.data?.weirGeometry as IWeirGeometrySteppedWeirForm).foundationLayers!) *
                    (project.data?.weirGeometry as IWeirGeometrySteppedWeirForm).stepHeight,
            ),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_stepped_sloped_discharge'),
            symbol: 'Q',
            value: roundToTwo(project.data?.hydraulicData?.discharge),
            unit: conversionLabel.m3s_ft3s[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_stepped_specific_discharge_crest'),
            symbol: <SmallLetter normal='q' small='c' />,
            value: roundToTwo(project.data?.hydraulicData?.discharge! / (project.data?.wingsGeometry as IWingsGeometrySteppedWeirForm).width),
            unit: conversionLabel.m3sm_ft3sft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_stepped_specific_discharge_toe'),
            symbol: <SmallLetter normal='q' small='t' />,
            value: roundToTwo(project.data?.hydraulicData?.discharge! / roundToTwo((project.data?.stillingBasin as IStillingBasinSteppedWeirSlopedWeirForm).width)),
            unit: conversionLabel.m3sm_ft3sft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_stepped_discharge_coefficient'),
            symbol: 'µ',
            value: 0.385,
            unit: '-',
        },
        {
            label: t('output_hydraulic_stepped_average_slope'),
            symbol: 'V:H',
            value: roundToTwo((project.data?.weirGeometry as IWeirGeometrySteppedWeirForm).stepRatio),
            unit: '-',
        },
        {
            label: t('output_hydraulic_stepped_dissipation'),
            symbol: 'α',
            value: roundToTwo(results.data?.basinDesign.dissipation),
            unit: '-',
        },
        {
            label: t('output_hydraulic_stepped_sloped_roughnessCoefficient'),
            symbol: 'n',
            value: roundToTwo(project.data?.hydraulicData?.roughnessCoefficient),
            unit: '-',
        },
        {
            label: t('output_hydraulic_stepped_river_bed_gradient'),
            symbol: 'i',
            value: roundToTwo(project.data?.hydraulicData?.riverBedGradient),
            unit: '-',
        },
    ];
    const upstream: IData[] = [
        {
            label: t('output_hydraulic_stepped_backwater_depth'),
            symbol: (
                <div className='d-flex'>
                    <SmallLetter normal='d' small='0' />
                </div>
            ),
            value: roundToTwo(results.data?.hydraulicUpstream.backwaterDepth),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_stepped_critical_depth'),
            symbol: (
                <div className='d-flex'>
                    <SmallLetter normal='d' small='c' />
                </div>
            ),
            value: roundToTwo(results.data?.hydraulicUpstream.criticalDepth),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_stepped_critical_velocity'),
            symbol: <SmallLetter normal='V' small='k' />,
            value: roundToTwo(results.data?.hydraulicUpstream.criticalVelocity),
            unit: conversionLabel.ms_fts[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_stepped_specific_head_crest'),
            symbol: <SmallLetter normal='H' small='s' />,
            value: roundToTwo(results.data?.hydraulicUpstream.specificHeadOnTheCrest),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_stepped_total_head_crest'),
            symbol: <SmallLetter normal='H' small='t' />,
            value: roundToTwo(results.data?.hydraulicUpstream.totalHeadOnTheCrest),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_stepped_type_flow'),
            symbol: '-',
            value: results.data?.hydraulicUpstream.typeOfFlow!,
            unit: '-',
        },
    ];
    const downstream: IData[] = [
        {
            label: t('output_hydraulic_stepped_total_head_downstream'),
            symbol: (
                <div className='d-flex'>
                    <SmallLetter normal='H' small='t' />
                </div>
            ),
            value: roundToTwo(results.data?.hydraulicDownstream.totalHeadDownstream),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_stepped_critical_depth'),
            symbol: <SmallLetter normal='y' small='cr' />,
            value: roundToTwo(results.data?.hydraulicDownstream.criticalDepth),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_stepped_critical_velocity'),
            symbol: <SmallLetter normal='V' small='k' />,
            value: roundToTwo(results.data?.hydraulicDownstream.criticalVelocity),
            unit: conversionLabel.ms_fts[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_stepped_specific_head_toe'),
            symbol: <SmallLetter normal='H' small='s' />,
            value: roundToTwo(results.data?.hydraulicDownstream.specificHeadAtTheToe),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_stepped_water_depth_supercritical_flow'),
            symbol: <SmallLetter normal='z' small='1' />,
            value: roundToTwo(results.data?.hydraulicDownstream.waterDepth),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_stepped_froude_number'),
            symbol: <SmallLetter normal='F' small='r' />,
            value: roundToTwo(results.data?.hydraulicDownstream.froudeNumber),
            unit: '-',
        },
        {
            label: t('output_hydraulic_stepped_conjugate_depth'),
            symbol: <SmallLetter normal='z' small='2' />,
            value: roundToTwo(results.data?.hydraulicDownstream.conjugateDepth),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_stepped_hydraulic_radius_superctitical_flow'),
            symbol: <SmallLetter normal='R' small='h' />,
            value: roundToTwo(results.data?.hydraulicDownstream.hydraulicRadius),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_stepped_average_rate_friction'),
            symbol: 'if',
            value: roundToTwo(results.data?.hydraulicDownstream.averageRateOfFriction),
            unit: '-',
        },
        {
            label: t('output_hydraulic_stepped_hydraulic_jump_length'),
            symbol: <SmallLetter normal='L' small='j' />,
            value: roundToTwo(results.data?.hydraulicDownstream.hydraulicJumpLength),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_stepped_downstream_water_depth'),
            symbol: <SmallLetter normal='z' small='3' />,
            value: roundToTwo(results.data?.hydraulicDownstream.z3),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_stepped_downstream_velocity'),
            symbol: <SmallLetter normal='v' small='3' />,
            value: roundToTwo(results.data?.hydraulicDownstream.v3),
            unit: conversionLabel.ms_fts[projectInfo.data?.unitOfMeasure as number],
        },
    ];
    return (
        <div className='d-flex justify-content-center align-items-center gap-3 w-100 overflow-auto'>
            <div className='d-flex justify-content-evenly align-items-center gap-3 w-50 static-results'>
                <div className='d-flex justify-content-evenly align-items-center gap-3 w-100 static-results'>
                    <Table columns={columnsInput} data={input} className='w-100' />
                </div>
            </div>
            <div className='d-flex flex-column justify-content-evenly align-items-center gap-3 w-50 static-results'>
                <label>{t('output_hydraulic_sloped_output')}</label>
                <Table columns={columnsUpstream} data={upstream} className='w-100' />
                <Table columns={columnsDownstream} data={downstream} className='w-100' />
            </div>
        </div>
    );
};

export default HydraulicStepped;
